import { useWorkbook } from '@/context/WorkbookContext';
import { 
  WorkbookPage, 
  RuleHeader, 
  SectionTitle, 
  Paragraph, 
  Callout,
  ExampleBox,
  StepList,
  WorksheetInput,
  CheckboxItem,
  AffirmationBox,
  RecapBox,
  OptionalSection 
} from '@/components/WorkbookPage';
import { Heart, Sparkles } from 'lucide-react';

export const Rule12Page = () => {
  const { formData, updateFormData } = useWorkbook();
  const data = formData.rule12 || {};
  
  const updateData = (field, value) => {
    updateFormData('rule12', { ...data, [field]: value });
  };
  
  return (
    <WorkbookPage pageId="rule-12" prevPage="section-4-divider" nextPage="rule-13">
      <RuleHeader ruleNumber="12" title="Heal Your Money Story" section="healing" />
      
      <SectionTitle>What This Rule Means</SectionTitle>
      <Paragraph>
        We all have a "money story"—beliefs, fears, and patterns we learned from childhood, 
        culture, and past experiences. These stories often run in the background, influencing 
        our decisions without us even knowing. Healing your money story means becoming aware 
        of these patterns and choosing new ones.
      </Paragraph>
      
      <div className="my-8 bg-primary/5 border border-primary/20 rounded-xl p-6">
        <div className="flex items-start gap-4">
          <Heart className="h-6 w-6 text-primary shrink-0 mt-1" />
          <div>
            <h3 className="font-semibold mb-2">A Gentle Reminder</h3>
            <p className="text-sm text-muted-foreground">
              This section may bring up difficult emotions. That's okay—and even healthy. 
              If you need to take a break, do so. If you find this triggering, consider 
              working through it with a trusted friend, counselor, or support group. 
              <strong> You don't have to do this alone.</strong>
            </p>
          </div>
        </div>
      </div>
      
      <SectionTitle>Common Money Stories</SectionTitle>
      
      <div className="grid gap-4 my-6">
        {[
          { story: '"Money is the root of all evil"', truth: 'Money is neutral—it amplifies who you are.' },
          { story: '"We\'re just not money people"', truth: 'Anyone can learn to manage money well.' },
          { story: '"I don\'t deserve to have money"', truth: 'You deserve stability and security.' },
          { story: '"Rich people are greedy"', truth: 'Wealth allows you to help others more.' },
          { story: '"I\'ll never get ahead"', truth: 'Small steps compound over time.' },
          { story: '"Talking about money is rude"', truth: 'Open conversations lead to better decisions.' },
        ].map((item, index) => (
          <div key={index} className="border border-border rounded-lg p-4">
            <p className="text-sm font-medium text-muted-foreground mb-1">Old story:</p>
            <p className="italic mb-2">{item.story}</p>
            <p className="text-sm font-medium text-success mb-1">New truth:</p>
            <p className="text-sm">{item.truth}</p>
          </div>
        ))}
      </div>
      
      <SectionTitle>Where Money Stories Come From</SectionTitle>
      <Paragraph>
        Your money story likely came from:
      </Paragraph>
      <ul className="list-disc list-inside space-y-2 mb-6">
        <li>What you heard adults say about money growing up</li>
        <li>How your family handled (or didn't handle) money</li>
        <li>Experiences of scarcity, instability, or abundance</li>
        <li>Cultural or religious messages about wealth</li>
        <li>Past financial mistakes or traumas</li>
        <li>Messages from media and society</li>
      </ul>
      
      <Callout type="tip" title="Awareness Is the First Step">
        You can't change a story you don't know you're telling. The worksheets below help 
        you identify your stories so you can consciously choose whether to keep them.
      </Callout>
      
      <ExampleBox title="Rewriting a Money Story">
        <p><strong>Old story:</strong> "I'm bad with money—I always mess up."</p>
        <p className="mt-2"><strong>Where it came from:</strong> Mom always said I was careless. 
        I made mistakes in my 20s and felt ashamed.</p>
        <p className="mt-2"><strong>New story:</strong> "I'm learning to manage money better. 
        Past mistakes taught me lessons. Every day I make better choices."</p>
      </ExampleBox>
      
      <StepList steps={[
        "Reflect on messages you received about money growing up.",
        "Notice patterns—do you avoid money? Fear it? Feel shame?",
        "Write down your current beliefs about money (be honest).",
        "Ask: Is this belief serving me, or holding me back?",
        "Choose a new belief that supports your goals.",
        "Practice the new belief through affirmations and action.",
        "Be patient—changing deep beliefs takes time."
      ]} />
      
      <div className="bg-muted/30 rounded-xl p-6 my-8 border border-border">
        <h3 className="font-serif font-bold text-lg mb-6">📝 Your Money Story Worksheet</h3>
        
        <div className="space-y-6">
          <WorksheetInput
            label="What did you hear about money growing up? (phrases, attitudes, etc.)"
            placeholder="Think about what adults around you said or showed..."
            value={data.childhoodMessages || ''}
            onChange={(v) => updateData('childhoodMessages', v)}
            multiline
          />
          
          <WorksheetInput
            label="How did your family handle money? (openly discussed? hidden? stressful?)"
            placeholder="Describe what money felt like in your household..."
            value={data.familyPatterns || ''}
            onChange={(v) => updateData('familyPatterns', v)}
            multiline
          />
          
          <WorksheetInput
            label="What beliefs about money do you currently hold? (be honest)"
            placeholder="'I believe money is...', 'I think I am... with money'"
            value={data.currentBeliefs || ''}
            onChange={(v) => updateData('currentBeliefs', v)}
            multiline
          />
          
          <WorksheetInput
            label="Which of these beliefs are holding you back?"
            placeholder="Which stories are NOT serving you?"
            value={data.limitingBeliefs || ''}
            onChange={(v) => updateData('limitingBeliefs', v)}
            multiline
          />
          
          <div className="bg-success/5 border border-success/20 rounded-lg p-4">
            <label className="block text-sm font-medium mb-3 flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-success" />
              Your new money story:
            </label>
            <textarea
              className="worksheet-textarea"
              placeholder="Write a new belief that supports your goals. 'I am capable of...', 'Money is a tool for...', 'I deserve...'"
              value={data.newStory || ''}
              onChange={(e) => updateData('newStory', e.target.value)}
              rows={4}
            />
          </div>
        </div>
      </div>
      
      <div className="my-8">
        <h3 className="font-semibold mb-4">Before moving on, confirm:</h3>
        <CheckboxItem checked={data.checklist1 || false} onChange={(v) => updateData('checklist1', v)}>
          I've reflected on where my money beliefs came from
        </CheckboxItem>
        <CheckboxItem checked={data.checklist2 || false} onChange={(v) => updateData('checklist2', v)}>
          I've identified at least one limiting belief
        </CheckboxItem>
        <CheckboxItem checked={data.checklist3 || false} onChange={(v) => updateData('checklist3', v)}>
          I've written a new, supportive money story
        </CheckboxItem>
      </div>
      
      <RecapBox items={[
        "We all have money stories from our past",
        "Awareness is the first step to change",
        "Old stories can be rewritten",
        "New beliefs need practice and patience",
        "You deserve a healthy relationship with money"
      ]} />
      
      <AffirmationBox>
        I release old stories that no longer serve me. I am writing a new chapter in my financial life.
      </AffirmationBox>
      
      <OptionalSection title="OPTIONAL: Deeper Reflection">
        <p className="mb-4">For those who want to go deeper:</p>
        <ul className="list-disc list-inside space-y-2 text-sm mb-4">
          <li>Is there a specific money memory that still affects you today?</li>
          <li>What would you tell your younger self about money?</li>
          <li>How might your life change if you fully believed your new story?</li>
        </ul>
        <WorksheetInput
          label="Your deeper reflections:"
          placeholder="Take your time with this..."
          value={data.deeperReflection || ''}
          onChange={(v) => updateData('deeperReflection', v)}
          multiline
        />
      </OptionalSection>
    </WorkbookPage>
  );
};
